import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Search, X } from "lucide-react";
import { useLang } from "@/i18n";
import { SERVICES } from "@/data/site";

export default function Services({ onBook }) {
  const { t } = useLang();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return SERVICES;
    return SERVICES.filter((s) =>
      s.name.toLowerCase().includes(query) || s.tags.some((tag) => tag.includes(query) || query.includes(tag))
    );
  }, [q]);

  return (
    <section id="services" className="py-20 md:py-28 bg-slate-50/60" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-turq-dark">{t("services_label")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-3 tracking-tight">{t("services_title")}</h2>
          <p className="text-secondary mt-4">{t("services_sub")}</p>
        </div>

        <div className="mt-10 max-w-xl mx-auto relative" data-testid="test-search">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")}
            className="w-full bg-white border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.05)] rounded-full pl-14 pr-12 py-4 text-base focus:ring-2 focus:ring-turq outline-none"
            data-testid="test-search-input" />
          {q && <button onClick={() => setQ("")} className="absolute right-5 top-1/2 -translate-y-1/2 text-muted hover:text-navy" data-testid="search-clear"><X className="w-5 h-5" /></button>}
        </div>

        {filtered.length === 0 ? (
          <p className="text-center text-secondary mt-10" data-testid="search-empty">No test found. Call us at the number above and we'll help you.</p>
        ) : (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="services-grid">
            {filtered.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.FlaskConical;
              return (
                <motion.button key={s.name} onClick={() => onBook(s.name)}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.04, 0.4) }}
                  className="group text-left bg-white rounded-2xl p-5 md:p-6 border border-slate-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_50px_-15px_rgba(0,180,216,0.25)] hover:-translate-y-1 transition-all duration-300"
                  data-testid={`service-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-turq/10 flex items-center justify-center text-turq-dark group-hover:bg-turq group-hover:text-white transition-colors">
                    <Icon className="w-6 h-6" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-heading font-semibold text-navy mt-4 text-base leading-snug">{s.name}</h3>
                  <span className="text-xs text-turq-dark font-medium mt-2 inline-block opacity-0 group-hover:opacity-100 transition-opacity">Book now →</span>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
